import type { HarvestLog } from '../../types/production'
import type { BiomassDataRow } from '../../utils/productionUnitTable'

import {
  buildDailyHarvestRollupKey,
  rollupHarvestLogsByLotDay,
  type DailyHarvestRollup
} from '../../utils/dailyHarvestRollup'
import { toLocalCalendarDayKey } from '../../utils/dateNormalization'
import { farmTimeZone } from '../../utils/farmTime'

type BiomassHarvestMetadata = {
  productionRowId?: string
  lotId?: string
  stage?: 'Active' | 'Inactive'
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

/**
 * Reverse of `buildBiomassHarvestMetadata`: extract the biomass-linkage
 * fields from a `HarvestLog.metadata` payload. Returns an empty object
 * when the metadata is missing, malformed, or lacks the expected keys.
 */
export const parseBiomassHarvestMetadata = (log: HarvestLog): BiomassHarvestMetadata => {
  const metadata = log.metadata
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return {}

  const stage = toTrimmedString(metadata.stage)
  let normalizedStage: 'Active' | 'Inactive' | undefined
  if (stage === 'Inactive') normalizedStage = 'Inactive'
  else if (stage === 'Active') normalizedStage = 'Active'

  return {
    productionRowId: toTrimmedString(metadata.productionRowId),
    lotId: toTrimmedString(metadata.lotId),
    stage: normalizedStage
  }
}

const toCalendarDateKey = (value: string | undefined | null, timeZone?: string): string => {
  // Local-day keying matches `overlayFishStockOnProductionRows` and the
  // MORT-12 / WSL overlays. Without this, evening rows in behind-UTC time
  // zones split off into their own bucket and the harvest trust signal
  // disagrees with what users see on the page.
  return toLocalCalendarDayKey(value, timeZone)
}

const buildRollupKeyForRow = (rowDate: string, lot: string | undefined, timeZone?: string): string => {
  return buildDailyHarvestRollupKey(toCalendarDateKey(rowDate, timeZone), String(lot ?? '').trim())
}

/**
 * Build the metadata payload written to a `HarvestLog` at save time so the
 * read side can promote the log captured *for* this row above the other
 * contributing logs. Mirrors `buildBiomassFishStockMetadata`.
 *
 * `source: 'biomassHarvest'` distinguishes harvest events tagged by the
 * biomass Cosecha panel from those tagged by other surfaces (e.g. bulk CSV
 * import) so downstream filters can pick their preferred provenance.
 */
export const buildBiomassHarvestMetadata = (args: {
  productionRowId: string
  lotId: string
  stage: 'Active' | 'Inactive'
}): Record<string, unknown> => ({
  source: 'biomassHarvest',
  productionRowId: args.productionRowId,
  lotId: args.lotId,
  stage: args.stage
})

/**
 * Find the single `HarvestLog` linked to this row for the
 * "edit-this-event" affordance. Prefers a metadata-tagged match (the log
 * captured *for* this row), then falls back to the first log matching
 * `(calendar date, lot)`.
 *
 * Intentionally 1:1. For the canonical *daily harvest total* (which is
 * SUM-PER-DAY across every event on the same day for the same lot) use
 * `findAllLinkedHarvestLogsForRow` or read the row's overlaid
 * `harvestKg` / `harvestCount` after `overlayHarvestOnProductionRows`.
 */
export const findLinkedHarvestLog = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logs: HarvestLog[]
): HarvestLog | undefined => {
  const rowDateKey = toCalendarDateKey(row.date)
  if (!rowDateKey) {
    return logs.find((log) => parseBiomassHarvestMetadata(log).productionRowId === row.id)
  }

  const rollupKey = buildRollupKeyForRow(row.date, row.lot)
  let fallbackMatch: HarvestLog | undefined

  for (const log of logs) {
    const metadata = parseBiomassHarvestMetadata(log)
    if (metadata.productionRowId === row.id) return log
    if (!fallbackMatch && buildRollupKeyForRow(log.recordedAt, log.lot, farmTimeZone(log.farmId)) === rollupKey) {
      fallbackMatch = log
    }
  }

  return fallbackMatch
}

/**
 * Return every `HarvestLog` whose `(calendar date, lot)` matches this row,
 * in input order, with the metadata-tagged log promoted to the front when
 * one exists. Used by trust-signal UI ("derived from N events") and any
 * consumer that needs per-event detail beyond the aggregated daily total.
 */
export const findAllLinkedHarvestLogsForRow = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logs: HarvestLog[]
): HarvestLog[] => {
  const rowDateKey = toCalendarDateKey(row.date)
  if (!rowDateKey) return []

  const rollupKey = buildRollupKeyForRow(row.date, row.lot)
  const matched: HarvestLog[] = []
  let metadataMatchIndex = -1

  for (const log of logs) {
    if (buildRollupKeyForRow(log.recordedAt, log.lot, farmTimeZone(log.farmId)) !== rollupKey) continue
    const metadata = parseBiomassHarvestMetadata(log)
    if (metadataMatchIndex === -1 && metadata.productionRowId === row.id) {
      metadataMatchIndex = matched.length
    }
    matched.push(log)
  }

  if (metadataMatchIndex > 0) {
    const [primary] = matched.splice(metadataMatchIndex, 1)
    matched.unshift(primary)
  }

  return matched
}

const pickPrimaryLogForRow = (
  rowId: string,
  rollup: DailyHarvestRollup,
  logsById: Map<string, HarvestLog>
): HarvestLog | undefined => {
  // Prefer a metadata-tagged log for this row, then the most recently
  // recorded contributing log so the "edit this event" affordance points at
  // something operationally meaningful. Mirrors FSL's `pickPrimaryLogForRow`.
  for (const logId of rollup.logIds) {
    const log = logsById.get(logId)
    if (!log) continue
    if (parseBiomassHarvestMetadata(log).productionRowId === rowId) return log
  }
  for (let i = rollup.logIds.length - 1; i >= 0; i -= 1) {
    const log = logsById.get(rollup.logIds[i])
    if (log) return log
  }
  return undefined
}

/**
 * Overlay the canonical daily harvest total onto each production row.
 *
 * For every row that has at least one matching `HarvestLog` (matched by
 * `(calendar date, lot)`), the row's `harvestKg` is replaced with the SUM
 * of `harvestKg` across contributing logs and `harvestCount` with the SUM
 * of `harvestCount`. This parallels the FSL / WSL / MORT-12 overlays:
 * events are canonical when they exist; stored row fields become
 * display-derived only.
 *
 * The row also carries:
 * - `harvestLogId` — id of the metadata-tagged log for that row, or the
 *   most recent contributing log when no metadata-tagged log exists.
 *   Anchors the "edit this event" affordance.
 * - `harvestLogCount` — string-encoded number of contributing logs. UI
 *   consumers parse this to render the "derived from N events" trust badge.
 *
 * Rows without matching logs are returned untouched (referential equality
 * preserved) — critical for React memoization in the chained overlay
 * pipeline shared with MORT-12, WSL, and FSL.
 */
export const overlayHarvestOnProductionRows = (
  rows: BiomassDataRow[],
  logs: HarvestLog[]
): BiomassDataRow[] => {
  if (rows.length === 0 || logs.length === 0) return rows

  const rollupByKey = rollupHarvestLogsByLotDay(logs)
  if (rollupByKey.size === 0) return rows

  const logsById = new Map<string, HarvestLog>()
  for (const log of logs) {
    if (typeof log.id === 'string' && log.id) {
      logsById.set(log.id, log)
    }
  }

  let changed = false
  const nextRows = rows.map((row) => {
    const key = buildRollupKeyForRow(row.date, row.lot)
    const rollup = rollupByKey.get(key)
    if (!rollup) return row

    const primaryLog = pickPrimaryLogForRow(row.id, rollup, logsById)
    if (!primaryLog) return row

    const metadata = parseBiomassHarvestMetadata(primaryLog)
    const nextHarvestKg = rollup.totalHarvestKg
    const nextHarvestCount = rollup.totalHarvestCount
    const nextLotId = row.lotId ?? metadata.lotId
    const nextStage = metadata.stage ?? row.stage
    const nextHarvestLogId = primaryLog.id
    const nextHarvestLogCount = String(rollup.eventCount)

    const rowAny = row as BiomassDataRow & {
      harvestKg?: string
      harvestCount?: string
      harvestLogId?: string
      harvestLogCount?: string
    }

    if (
      rowAny.harvestKg === nextHarvestKg
      && rowAny.harvestCount === nextHarvestCount
      && row.lotId === nextLotId
      && row.stage === nextStage
      && rowAny.harvestLogId === nextHarvestLogId
      && rowAny.harvestLogCount === nextHarvestLogCount
    ) {
      return row
    }

    changed = true
    return {
      ...row,
      harvestKg: nextHarvestKg,
      harvestCount: nextHarvestCount,
      lotId: nextLotId,
      stage: nextStage,
      harvestLogId: nextHarvestLogId,
      harvestLogCount: nextHarvestLogCount
    }
  })

  return changed ? nextRows : rows
}

/**
 * Sidecar map from production-row id to the canonical event-derived
 * harvest rollup for that `(calendar date, lot)`. Returned without
 * mutating the input rows so screens that need to keep `data` as their
 * editable source of truth (notably the Database screen) can render trust
 * signals next to the stored value instead of overwriting it.
 *
 * Rows whose `(calendar date, lot)` does not match any log are omitted.
 */
export const computeHarvestRollupByRowId = (
  rows: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>[],
  logs: HarvestLog[]
): Map<string, {
  totalHarvestKg: string
  totalHarvestCount: string
  eventCount: number
  primaryLogId: string | undefined
  aggregatedMethod: 'count' | 'estimation'
}> => {
  const out = new Map<string, {
    totalHarvestKg: string
    totalHarvestCount: string
    eventCount: number
    primaryLogId: string | undefined
    aggregatedMethod: 'count' | 'estimation'
  }>()
  if (rows.length === 0 || logs.length === 0) return out

  const rollupByKey = rollupHarvestLogsByLotDay(logs)
  if (rollupByKey.size === 0) return out

  const logsById = new Map<string, HarvestLog>()
  for (const log of logs) {
    if (typeof log.id === 'string' && log.id) logsById.set(log.id, log)
  }

  for (const row of rows) {
    const key = buildRollupKeyForRow(row.date, row.lot)
    const rollup = rollupByKey.get(key)
    if (!rollup || rollup.eventCount === 0) continue
    const primary = pickPrimaryLogForRow(row.id, rollup, logsById)
    out.set(row.id, {
      totalHarvestKg: rollup.totalHarvestKg,
      totalHarvestCount: rollup.totalHarvestCount,
      eventCount: rollup.eventCount,
      primaryLogId: primary?.id,
      aggregatedMethod: rollup.aggregatedMethod
    })
  }

  return out
}
